/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { take } from 'rxjs/operators'
import { DynamicFormControlLayout } from '../../../model/misc/dynamic-form-control-layout.model'
import { DynamicSelectModel } from '../../../model/select/dynamic-select.model'
import { DynamicFormLayout, DynamicFormLayoutService } from '../../../service/dynamic-form-layout.service'
import { DynamicFormValidationService } from '../../../service/dynamic-form-validation.service'
import { BaseFormControlComponent } from '../base-form-control.component'

@Component({
  selector: 'dynamic-basic-select',
  templateUrl: './dynamic-basic-select.component.html',
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DynamicBasicSelectComponent extends BaseFormControlComponent implements OnInit {
  // @ts-ignore
  layoutService: DynamicFormLayoutService
  // @ts-ignore
  validationService: DynamicFormValidationService

  // @ts-ignore
  @Input() formLayout: DynamicFormLayout
  // @ts-ignore
  @Input() group: UntypedFormGroup
  // @ts-ignore
  @Input() layout: DynamicFormControlLayout
  // @ts-ignore
  @Input() model: DynamicSelectModel<string>

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur: EventEmitter<any> = new EventEmitter()
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() change: EventEmitter<any> = new EventEmitter()
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() focus: EventEmitter<any> = new EventEmitter()

  public mode = DropdownMode.DEFAULT

  public modelOptions: SelectOption[] = []

  public hasDeselection = false

  public deselectionLabel: string | null = null

  public renderItems: SelectRenderItem[] = []

  protected isGroupRenderItem(item: SelectRenderItem): item is SelectGroupRenderItem {
    return item.group !== null
  }

  constructor() {
    const layoutService = inject(DynamicFormLayoutService)
    const validationService = inject(DynamicFormValidationService)

    super()

    this.layoutService = layoutService
    this.validationService = validationService
  }

  public ngOnInit(): void {
    this.model.options$.pipe(take(1)).subscribe((options) => {
      // Ensure all option values are strings ('' for null/undefined)
      this.modelOptions = options.map((option) => ({
        ...option,
        value: option.value == null ? '' : String(option.value),
      }))

      // A "NoSelection" option (empty value) is rendered as PDS' native deselection
      // (a valueless select-option) and excluded from the regular option list.
      const deselectionOption = this.modelOptions.find((option) => option.value === '')
      this.hasDeselection = !!deselectionOption
      this.deselectionLabel = deselectionOption?.label ?? null

      const selectableOptions = this.modelOptions.filter((option) => option.value !== '')

      this.renderItems = this.buildRenderItems(selectableOptions)
      this.mode = options.length && options.length >= 5 ? DropdownMode.SEARCH : DropdownMode.DEFAULT
    })
  }

  private buildRenderItems(options: SelectOption[]): SelectRenderItem[] {
    const items: SelectRenderItem[] = []
    const groupItems = new Map<string, SelectGroupRenderItem>()

    options.forEach((option) => {
      if (option.group) {
        let groupItem = groupItems.get(option.group)

        if (!groupItem) {
          groupItem = { group: option.group, options: [] }
          groupItems.set(option.group, groupItem)
          items.push(groupItem)
        }

        groupItem.options.push(option)
      } else {
        items.push({ group: null, option })
      }
    })

    return items
  }
}

export interface SelectOption {
  value: string
  label: string | null
  disabled: boolean
  group?: string | null
}

export interface SelectGroupRenderItem {
  group: string
  options: SelectOption[]
}

export interface SelectSingleRenderItem {
  group: null
  option: SelectOption
}

export type SelectRenderItem = SelectGroupRenderItem | SelectSingleRenderItem

export enum DropdownMode {
  DEFAULT = 'DEFAULT',
  SEARCH = 'SEARCH',
  AUTOCOMPLETE = 'AUTOCOMPLETE',
}
